import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { ArrowUpRight, AlertTriangle, Coins, Clock } from "lucide-react";
import { usePermission } from "@/lib/usePermission";

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Phase TOLLBILL-P3b → P5d (2026-07-10) — Unbilled Toll BILLS dashboard
 * widget.
 *
 * P5d — swapped from transactions to BILLS (bills are first-class as of
 * P5a). Renders three counts + a full-value total on the admin
 * dashboard, gated by ``Tolls / View``.
 *
 * Data source: three parallel calls to ``GET /admin/tolls/bills`` with
 * narrow filters:
 *   - Unbilled (matched, undisputed, no invoice_id) — 200-row page + sum total_due_cents client-side
 *   - Disputed bills — limit=1 just for the ``total`` pagination field
 *   - Unmatched bills — limit=1 just for the ``total`` pagination field
 *
 * The dollar figure reflects the FULL value including processing fees,
 * late fees, and previous balances (``total_due_cents``) — not just the
 * toll base. This matches how the invoicing generator persists.
 *
 * If unbilled bills exceed our 200-row page cap, a "+" suffix signals
 * that the true number is higher; click through to /admin/tolls/bills
 * to see the exact figure.
 */
export default function TollsUnbilledDashboardWidget() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [unbilledCount, setUnbilledCount] = useState(0);
    const [unbilledCents, setUnbilledCents] = useState(0);
    const [unbilledCapped, setUnbilledCapped] = useState(false);
    const [disputedCount, setDisputedCount] = useState(0);
    const [unmatchedCount, setUnmatchedCount] = useState(0);
    const [overdueCount, setOverdueCount] = useState(0);

    const { ready: permReady, hasPermission } = usePermission();
    const canView = hasPermission("Tolls", "View");

    useEffect(() => {
        if (!permReady || !canView) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [unbilled, disputed, unmatched] = await Promise.all([
                    api.get("/admin/tolls/bills", {
                        params: { status: "matched", dispute_flag: false, limit: 200 },
                    }),
                    api.get("/admin/tolls/bills", {
                        params: { status: "disputed", limit: 1 },
                    }),
                    api.get("/admin/tolls/bills", {
                        params: { status: "unmatched", limit: 1 },
                    }),
                ]);
                if (cancelled) return;
                const ubItems = unbilled.data.items || [];
                // Backend doesn't expose an ``invoice_id null`` filter,
                // so filter client-side. matched ∧ !invoice_id ∧
                // !dispute_flag was the API-side filter — we only need
                // to guard invoice_id.
                const ubTruly = ubItems.filter((b) => !b.invoice_id);
                const ubTotalCents = ubTruly.reduce(
                    (sum, b) => sum + Number(b.total_due_cents || 0), 0,
                );
                setUnbilledCount(ubTruly.length);
                setUnbilledCents(ubTotalCents);
                setUnbilledCapped((unbilled.data.total || 0) > ubItems.length);
                setDisputedCount(disputed.data.total || 0);
                setUnmatchedCount(unmatched.data.total || 0);
                // Overdue = bills with due_date < today AND status
                // not in {invoiced, void}. We infer from the unbilled
                // slice we already fetched (matched, so definitely not
                // terminal). This is a soft count — for full accuracy
                // ops can drill into /admin/tolls/bills.
                const today = new Date().toISOString().slice(0, 10);
                const overdue = ubTruly.filter(
                    (b) => b.due_date && String(b.due_date) < today,
                ).length;
                setOverdueCount(overdue);
            } catch (err) {
                if (!cancelled) setError(formatApiError(err));
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [permReady, canView]);

    if (permReady && !canView) return null;

    return (
        <Card className="mt-4 border-l-4 border-l-blue-900" data-testid="dashboard-tolls-widget">
            <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-900">
                            <Coins size={18} />
                        </span>
                        <div>
                            <p className="text-xs uppercase tracking-wider text-blue-900 font-semibold">Ancillary billing</p>
                            <h3 className="font-display text-lg font-semibold text-slate-900">Unbilled toll bills</h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Matched toll bills ready to invoice — full value including fees. Plus dispute / unmatched review counts.
                            </p>
                        </div>
                    </div>
                    <Link
                        to="/admin/tolls/bills"
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-900 hover:underline"
                        data-testid="dashboard-tolls-manage-link"
                    >
                        Manage bills <ArrowUpRight size={14} />
                    </Link>
                </div>

                {error && <p className="text-xs text-red-700 mt-3" data-testid="dashboard-tolls-error">{error}</p>}

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2" data-testid="dashboard-tolls-unbilled">
                        <p className="text-[11px] uppercase tracking-wider text-blue-900/80">Unbilled total</p>
                        <p className="text-2xl font-display font-bold text-slate-900 tabular-nums">
                            {loading ? "…" : (
                                <>
                                    {fmtMoney(unbilledCents)}
                                    {unbilledCapped && <span className="text-sm text-slate-500 ml-1">+</span>}
                                </>
                            )}
                        </p>
                        <p className="text-[11px] text-slate-600 mt-0.5" data-testid="dashboard-tolls-unbilled-count">
                            {loading ? "" : `${unbilledCount} bill${unbilledCount === 1 ? "" : "s"}`}
                        </p>
                    </div>
                    <div className={`rounded-md px-3 py-2 border ${disputedCount > 0 ? "bg-red-50/60 border-red-100" : "bg-slate-50 border-slate-200"}`} data-testid="dashboard-tolls-disputed">
                        <p className={`text-[11px] uppercase tracking-wider ${disputedCount > 0 ? "text-red-700" : "text-slate-500"}`}>
                            {disputedCount > 0 && <AlertTriangle size={11} className="inline mr-1" />}
                            Disputed
                        </p>
                        <p className="text-2xl font-display font-bold text-slate-900 tabular-nums">
                            {loading ? "…" : disputedCount}
                        </p>
                        <p className="text-[11px] text-slate-600 mt-0.5">bill{disputedCount === 1 ? "" : "s"} in review</p>
                    </div>
                    <div className={`rounded-md px-3 py-2 border ${unmatchedCount > 0 ? "bg-amber-50/60 border-amber-100" : "bg-slate-50 border-slate-200"}`} data-testid="dashboard-tolls-unmatched">
                        <p className={`text-[11px] uppercase tracking-wider ${unmatchedCount > 0 ? "text-amber-800" : "text-slate-500"}`}>
                            Unmatched
                        </p>
                        <p className="text-2xl font-display font-bold text-slate-900 tabular-nums">
                            {loading ? "…" : unmatchedCount}
                        </p>
                        <p className="text-[11px] text-slate-600 mt-0.5">need reassignment</p>
                    </div>
                    <div className={`rounded-md px-3 py-2 border ${overdueCount > 0 ? "bg-red-50/60 border-red-100" : "bg-slate-50 border-slate-200"}`} data-testid="dashboard-tolls-overdue">
                        <p className={`text-[11px] uppercase tracking-wider ${overdueCount > 0 ? "text-red-700" : "text-slate-500"}`}>
                            {overdueCount > 0 && <Clock size={11} className="inline mr-1" />}
                            Overdue
                        </p>
                        <p className="text-2xl font-display font-bold text-slate-900 tabular-nums">
                            {loading ? "…" : overdueCount}
                        </p>
                        <p className="text-[11px] text-slate-600 mt-0.5">past due date</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
